import { Tenant } from '@innovation-os-ng/core/models';
import { createReducer, on } from '@ngrx/store';
import { getInitialMetaState } from '../interfaces/meta-state.interface';
import { TenantState, TennatCRUDMessages } from '../interfaces/tenant-state.interface';
import { TenantActionTypes, TenantActions } from './tenant.actions';

export const _404_TENANT_SLUG = '404 / TENANT SLUG DOES NOT EXIST /'

export const initialTenantState: TenantState = {
  tenants: [],
  currentTenant: null,
  currentTenantSlug: null,
  ...getInitialMetaState<Tenant, TennatCRUDMessages>()
};

const reducer = createReducer<TenantState>(
  initialTenantState,
  // ##################### fetch tenants
  on(TenantActions.fetchTenants, (state): TenantState => {
    return {
      ...state,
      isLoading: true,
      result: {
        ...state.result,
        collectionLoaded: null
      }
    };
  }),
  on(TenantActions.fetchTenantsSuccess, (state, { tenants }): TenantState => {
    return {
      ...state,
      tenants,
      isLoading: false,
      currentTenantSlug: state?.currentTenant?.tenantSlug,
      result: {
        ...state.result,
        collectionLoaded: {
          message: 'collectionLoaded'
        }
      }
    };
  }),
  on(TenantActions.fetchTenantsError, (state, { error }): TenantState => {
    return {
      ...state,
      isLoading: false,
      result: {
        ...state.result,
        collectionLoaded: {
          message: 'loadError',
          error
        }
      }
    };
  }),
  // ##################### create tenant
  on(TenantActions.createTenant, (state): TenantState => {
    return {
      ...state,
      isCreating: true,
      result: {
        ...state.result,
        created: null
      }
    };
  }),
  on(TenantActions.createTenantSuccess, (state, { tenant }): TenantState => {
    return {
      ...state,
      isCreating: false,
      result: {
        ...state.result,
        created: {
          message: 'created',
          data: tenant
        }
      }
    };
  }),
  on(TenantActions.createTenantError, (state, { error }): TenantState => {
    return {
      ...state,
      isCreating: false,
      result: {
        created: {
          message: 'createError',
          error
        }
      }
    };
  }),
  // ##################### update tenant
  on(TenantActions.updateTenant, (state): TenantState => {
    return {
      ...state,
      isUpdating: true,
      result: {
        ...state.result,
        updated: null
      }
    };
  }),
  on(TenantActions.updateTenantSuccess, (state, { tenant, metadata }): TenantState => {
    return {
      ...state,
      isUpdating: false,
      tenants: state.tenants.map(t => t.tenantUri === tenant.tenantUri ? tenant : t),
      result: {
        ...state.result,
        updated: {
          message: 'updated',
          data: { ...tenant, metadata }
        }
      }
    };
  }),
  on(TenantActions.updateTenantError, (state, { error, metadata }): TenantState => {
    return {
      ...state,
      isUpdating: false,
      result: {
        ...state.result,
        updated: {
          message: 'updateError',
          error: { ...error, metadata }
        }
      }
    };
  }),
  // ##################### delete tenant
  on(TenantActions.deleteTenant, (state):TenantState => {
    return {
      ...state,
      isDeleting: true,
      result: {
        ...state.result,
        deleted: null
      }
    };
  }),
  on(TenantActions.deleteTenantSuccess, (state): TenantState => {
    return {
      ...state,
      isDeleting: false,
      result: {
        ...state.result,
        deleted: {
          message: 'deleted'
        }
      }
    };
  }),
  on(TenantActions.deleteTenantError, (state, { error }): TenantState => {
    return {
      ...state,
      isDeleting: false,
      result: {
        ...state.result,
        deleted: {
          message: 'deleteError',
          error
        }
      }
    };
  }),
  // ##################### set current tenant
  on(TenantActions.setCurrentTenant, (state, { tenant }): TenantState => {
    return {
      ...state,
      currentTenant: tenant
    };
  }),
  // ##################### activate a tenant
  on(TenantActions.activateTenant, (state): TenantState => {
    return {
      ...state,
      isUpdating: true,
      result: {
        ...state.result,
        updated: null
      }
    };
  }),
  // ##################### deactivate a tenant
  on(TenantActions.deactivateTenant, (state): TenantState => {
    return {
      ...state,
      isUpdating: true,
      result: {
        ...state.result,
        updated: null
      }
    };
  }),
  on(TenantActions.reset, (state): TenantState => {
    console.debug('Action dispatched: ', TenantActions.reset.type);
    return {
      ...state,
      ...initialTenantState
    };
  }),
  on(TenantActions.setCurrentTenantSlug, (state, { tenantSlug }): TenantState => {
    console.debug('Action dispatched: ', TenantActions.setCurrentTenantSlug.type);
    return {
      ...state,
      currentTenantSlug: tenantSlug
    };
  }),
  on(TenantActions.addTenantToListOfTenants, (state, { tenant }): TenantState => {
    console.debug('Action dispatched: ', TenantActions.addTenantToListOfTenants.type);
    if (state.tenants.find(t => t.tenantUri === tenant.tenantUri)) {
      return state
    } else {
      return {
        ...state,
        tenants: [...state.tenants, tenant]
      };
    }
  }),
  on(TenantActions.onTenants404, (state): TenantState => {
    console.debug('Action dispatched: ', TenantActions.onTenants404.type);
    return {
      ...state,
      tenants: [],
      currentTenantSlug: _404_TENANT_SLUG
    };
  })
);

export const TenantReducer = (state = initialTenantState, actions: TenantActionTypes): TenantState => {
  return reducer(state, actions);
};
